import { useState } from 'react'

export interface UpdateCompanyRequest {
  name?: string
  description?: string
  website?: string
  logoUrl?: string
}

export interface Company {
  id: string
  name: string
  description: string | null
  website: string | null
  logoUrl: string | null
  updatedAt: string
}

export interface UpdateCompanyResponse {
  message: string
  company: Company
}

export interface UseUpdateCompanyResult {
  updateCompany: (companyId: string, request: UpdateCompanyRequest) => Promise<UpdateCompanyResponse>
  loading: boolean
  error: string | null
}

export function useUpdateCompany(): UseUpdateCompanyResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateCompany = async (companyId: string, request: UpdateCompanyRequest): Promise<UpdateCompanyResponse> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`/api/admin/companies/${companyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to update company (${response.status})`)
      }

      const data: UpdateCompanyResponse = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update company'
      setError(errorMessage)
      console.error('Error updating company:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    updateCompany,
    loading,
    error
  }
}
